import React, { useMemo, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { calculateGameRating } from '../../utils/gameRatingUtils';

/**
 * Simulation Summary component showing KPIs and game rating trends
 */
const SimulationSummary = ({
  gamesData = [],
  playerInteractions = null,
  gamesPlayedThisSession = null,
  summaryData = [],
}) => {
  // State to track which lines are visible
  const [visibleLines, setVisibleLines] = useState({
    total: true,
    skillEvenness: true,
    categorization: true,
    interactions: true,
    gameTypeBalance: true,
  });
  // Calculate game ratings for all games
  const gamesWithRatings = useMemo(() => {
    if (!gamesData || gamesData.length === 0) return [];
    
    return gamesData.map((game, index) => {
      const rating = (playerInteractions && gamesPlayedThisSession)
        ? calculateGameRating(game, {
            playerInteractions: game.preGameInteractions || playerInteractions,
            gamesPlayedThisSession: game.preGameGamesPlayed || gamesPlayedThisSession,
          })
        : null;
      
      return {
        gameNumber: game.game_number || index + 1,
        rating: rating,
        total: rating?.total || 0,
        skillEvenness: rating?.skillEvenness || 0,
        categorization: rating?.categorization || 0,
        interactions: rating?.interactions || 0,
        gameTypeBalance: rating?.gameTypeBalance || 0,
      };
    });
  }, [gamesData, playerInteractions, gamesPlayedThisSession]);

  // Count players with high interactions (>=50% interaction with at least one other player)
  const playersWithHighInteractions = useMemo(() => {
    if (!summaryData || summaryData.length === 0) return 0;
    
    return summaryData.filter(player => {
      // Check if player has >=50% interaction with any other player
      const totalGames = player.total_games || 0;
      if (totalGames === 0) return false;
      
      return Object.entries(player.interaction_counts || {}).some(([otherPlayerId, count]) => {
        const percentage = (count / totalGames) * 100;
        return percentage >= 50;
      });
    }).length;
  }, [summaryData]);

  // Calculate KPIs
  const kpis = useMemo(() => {
    const totalGames = gamesWithRatings.length;
    
    // Count games with 4-5 rating for each criterion
    const skillEvennessHigh = gamesWithRatings.filter(g => g.skillEvenness >= 4).length;
    const categorizationHigh = gamesWithRatings.filter(g => g.categorization >= 4).length;
    const interactionsHigh = gamesWithRatings.filter(g => g.interactions >= 4).length;
    const gameTypeBalanceHigh = gamesWithRatings.filter(g => g.gameTypeBalance >= 4).length;
    
    // Calculate average total rating
    const avgTotalRating = totalGames > 0
      ? gamesWithRatings.reduce((sum, g) => sum + g.total, 0) / totalGames
      : 0;
    
    // Calculate average for each criterion
    const avgSkillEvenness = totalGames > 0
      ? gamesWithRatings.reduce((sum, g) => sum + g.skillEvenness, 0) / totalGames
      : 0;
    const avgCategorization = totalGames > 0
      ? gamesWithRatings.reduce((sum, g) => sum + g.categorization, 0) / totalGames
      : 0;
    const avgInteractions = totalGames > 0
      ? gamesWithRatings.reduce((sum, g) => sum + g.interactions, 0) / totalGames
      : 0;
    const avgGameTypeBalance = totalGames > 0
      ? gamesWithRatings.reduce((sum, g) => sum + g.gameTypeBalance, 0) / totalGames
      : 0;
    
    // Calculate percentages for each criterion
    const skillEvennessPercent = totalGames > 0 ? ((skillEvennessHigh / totalGames) * 100).toFixed(0) : 0;
    const categorizationPercent = totalGames > 0 ? ((categorizationHigh / totalGames) * 100).toFixed(0) : 0;
    const interactionsPercent = totalGames > 0 ? ((interactionsHigh / totalGames) * 100).toFixed(0) : 0;
    const gameTypeBalancePercent = totalGames > 0 ? ((gameTypeBalanceHigh / totalGames) * 100).toFixed(0) : 0;

    return {
      totalGames,
      skillEvennessHigh,
      categorizationHigh,
      interactionsHigh,
      gameTypeBalanceHigh,
      skillEvennessPercent,
      categorizationPercent,
      interactionsPercent,
      gameTypeBalancePercent,
      playersWithHighInteractions: playersWithHighInteractions,
      avgTotalRating: avgTotalRating.toFixed(1),
      avgSkillEvenness: avgSkillEvenness.toFixed(2),
      avgCategorization: avgCategorization.toFixed(2),
      avgInteractions: avgInteractions.toFixed(2),
      avgGameTypeBalance: avgGameTypeBalance.toFixed(2),
    };
  }, [gamesWithRatings, playersWithHighInteractions]);

  // Prepare chart data
  const chartData = useMemo(() => {
    return gamesWithRatings.map(game => ({
      game: game.gameNumber,
      total: game.total,
      skillEvenness: game.skillEvenness,
      categorization: game.categorization,
      interactions: game.interactions,
      gameTypeBalance: game.gameTypeBalance,
    }));
  }, [gamesWithRatings]);

  // Toggle line visibility
  const handleLegendClick = (dataKey) => {
    setVisibleLines(prev => ({
      ...prev,
      [dataKey]: !prev[dataKey],
    }));
  };

  // Define legend items statically (not dependent on Recharts payload)
  const legendItems = [
    { dataKey: 'total', name: 'Total Rating', color: '#3b82f6' },
    { dataKey: 'skillEvenness', name: 'Skill Evenness', color: '#10b981' },
    { dataKey: 'categorization', name: 'Categorization', color: '#8b5cf6' },
    { dataKey: 'interactions', name: 'Interactions', color: '#f59e0b' },
    { dataKey: 'gameTypeBalance', name: 'Game Type Balance', color: '#ef4444' },
  ];

  // Custom legend with click handlers - always shows all items
  const CustomLegend = () => {
    return (
      <div className="flex justify-center items-center gap-6 mt-4 flex-wrap">
        {legendItems.map((item, index) => {
          const isVisible = visibleLines[item.dataKey];
          return (
            <div
              key={item.dataKey}
              onClick={() => handleLegendClick(item.dataKey)}
              className={`flex items-center cursor-pointer transition-all hover:opacity-80 ${
                isVisible ? '' : 'opacity-50'
              }`}
              title={isVisible ? 'Click to hide' : 'Click to show'}
            >
              <div
                className="w-4 h-4 mr-2 rounded-sm border"
                style={{
                  backgroundColor: isVisible ? item.color : '#d1d5db',
                  borderColor: isVisible ? item.color : '#9ca3af',
                }}
              />
              <span 
                className={`text-sm font-medium ${
                  isVisible ? 'text-text-secondary' : 'text-text-muted line-through'
                }`}
              >
                {item.name}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      // Only show visible lines in tooltip
      const visiblePayload = payload.filter(entry => visibleLines[entry.dataKey]);
      if (visiblePayload.length === 0) return null;
      
      return (
        <div className="bg-surface p-3 border border-border-medium rounded-lg shadow-lg">
          <p className="font-semibold mb-2 text-text-primary">Game {label}</p>
          {visiblePayload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* KPI Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Games */}
        <div className="bg-surface border border-border-medium rounded-lg p-4 shadow-sm">
          <div className="text-sm text-text-secondary mb-1">Total Games</div>
          <div className="text-3xl font-bold text-text-primary">{kpis.totalGames}</div>
        </div>

        {/* Average Total Rating */}
        <div className="bg-surface border border-border-medium rounded-lg p-4 shadow-sm">
          <div className="text-sm text-text-secondary mb-1">Avg Total Rating</div>
          <div className="text-3xl font-bold text-text-primary">{kpis.avgTotalRating}</div>
          <div className="text-xs text-text-muted mt-1">out of 20</div>
        </div>

        {/* Players with High Interactions */}
        <div className="bg-surface border border-border-medium rounded-lg p-4 shadow-sm">
          <div className="text-sm text-text-secondary mb-1">High Interaction Players</div>
          <div className="text-3xl font-bold text-text-primary">{kpis.playersWithHighInteractions}</div>
          <div className="text-xs text-text-muted mt-1">≥50% with someone</div>
        </div>

        {/* Overall Quality Score */}
        <div className="bg-surface border border-border-medium rounded-lg p-4 shadow-sm">
          <div className="text-sm text-text-secondary mb-1">Quality Games</div>
          <div className="text-3xl font-bold text-text-primary">
            {gamesWithRatings.filter(g => g.total >= 16).length}
          </div>
          <div className="text-xs text-text-muted mt-1">≥16 total rating</div>
        </div>
      </div>

      {/* Rating Criteria Breakdown */}
      <div className="bg-surface border border-border-medium rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Rating Criteria Breakdown</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Skill Evenness */}
          <div className="border border-border-medium rounded-lg p-4">
            <div className="text-sm font-medium text-text-secondary mb-2">Skill Evenness</div>
            <div className="text-2xl font-bold text-blue-600 mb-1">{kpis.skillEvennessHigh}</div>
            <div className="text-xs text-text-muted">
              Games with 4-5 rating ({kpis.skillEvennessPercent}%)
            </div>
            <div className="text-xs text-text-muted mt-1">Avg: {kpis.avgSkillEvenness}</div>
          </div>

          {/* Categorization */}
          <div className="border border-border-medium rounded-lg p-4">
            <div className="text-sm font-medium text-text-secondary mb-2">Categorization</div>
            <div className="text-2xl font-bold text-green-600 mb-1">{kpis.categorizationHigh}</div>
            <div className="text-xs text-text-muted">
              Games with 4-5 rating ({kpis.categorizationPercent}%)
            </div>
            <div className="text-xs text-text-muted mt-1">Avg: {kpis.avgCategorization}</div>
          </div>

          {/* Interactions */}
          <div className="border border-border-medium rounded-lg p-4">
            <div className="text-sm font-medium text-text-secondary mb-2">Interactions (Variety)</div>
            <div className="text-2xl font-bold text-purple-600 mb-1">{kpis.interactionsHigh}</div>
            <div className="text-xs text-text-muted">
              Games with 4-5 rating ({kpis.interactionsPercent}%)
            </div>
            <div className="text-xs text-text-muted mt-1">Avg: {kpis.avgInteractions}</div>
          </div>

          {/* Game Type Balance */}
          <div className="border border-border-medium rounded-lg p-4">
            <div className="text-sm font-medium text-text-secondary mb-2">Game Type Balance</div>
            <div className="text-2xl font-bold text-orange-600 mb-1">{kpis.gameTypeBalanceHigh}</div>
            <div className="text-xs text-text-muted">
              Games with 4-5 rating ({kpis.gameTypeBalancePercent}%)
            </div>
            <div className="text-xs text-text-muted mt-1">Avg: {kpis.avgGameTypeBalance}</div>
          </div>
        </div>
      </div>

      {/* Game Rating Over Time Chart */}
      <div className="bg-surface border border-border-medium rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-4">Game Rating Over Time</h3>
        <p className="text-sm text-text-secondary mb-4">
          Track how matchmaking quality changes throughout the session. A declining trend may indicate the need to adjust the matchmaking algorithm.
        </p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis 
                dataKey="game" 
                label={{ value: 'Game Number', position: 'insideBottom', offset: -5 }}
                stroke="#666"
                fontSize={12}
              />
              <YAxis 
                domain={[0, 20]}
                label={{ value: 'Rating', angle: -90, position: 'insideLeft' }}
                stroke="#666"
                fontSize={12}
              />
              <Tooltip content={<CustomTooltip />} />
              {/* Hide default legend, we'll render our own below */}
              <Legend wrapperStyle={{ display: 'none' }} />
              {visibleLines.total && (
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                  activeDot={{ r: 5 }}
                  name="Total Rating"
                />
              )}
              {visibleLines.skillEvenness && (
                <Line
                  type="monotone"
                  dataKey="skillEvenness"
                  stroke="#10b981"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Skill Evenness"
                />
              )}
              {visibleLines.categorization && (
                <Line
                  type="monotone"
                  dataKey="categorization"
                  stroke="#8b5cf6"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Categorization"
                />
              )}
              {visibleLines.interactions && (
                <Line
                  type="monotone"
                  dataKey="interactions"
                  stroke="#f59e0b"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Interactions"
                />
              )}
              {visibleLines.gameTypeBalance && (
                <Line
                  type="monotone"
                  dataKey="gameTypeBalance"
                  stroke="#ef4444"
                  strokeWidth={1.5}
                  strokeDasharray="5 5"
                  dot={false}
                  name="Game Type Balance"
                />
              )}
            </LineChart>
          </ResponsiveContainer>
          {/* Custom legend rendered outside chart to always be visible */}
          <CustomLegend />
        </div>
      </div>

      {/* Rating Criteria Explanation */}
      <div className="bg-surface border border-border-medium rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-text-primary mb-4">How Ratings Are Calculated</h3>
        <p className="text-sm text-text-secondary mb-6">
          Each game is rated on a scale of 1-5 for four criteria. Higher scores indicate better matchmaking quality.
        </p>
        
        <div className="space-y-6">
            {/* Skill Evenness */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="text-base font-semibold text-text-primary mb-2">1. Skill Evenness (ELO Balance)</h4>
              <p className="text-sm text-text-secondary mb-3">
                Measures how close the average ELO ratings are between the two teams.
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-blue-600 w-6">5</span>
                  <span className="text-text-secondary">ELO difference ≤ 50 points (excellent balance)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-blue-600 w-6">4</span>
                  <span className="text-text-secondary">ELO difference 51-100 points (good balance)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-blue-600 w-6">3</span>
                  <span className="text-text-secondary">ELO difference 101-150 points (moderate imbalance)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-blue-600 w-6">2</span>
                  <span className="text-text-secondary">ELO difference 151-200 points (poor balance)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-blue-600 w-6">1</span>
                  <span className="text-text-secondary">ELO difference > 200 points (very poor balance)</span>
                </div>
              </div>
            </div>

            {/* Player Categorization */}
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="text-base font-semibold text-text-primary mb-2">2. Player Categorization</h4>
              <p className="text-sm text-text-secondary mb-3">
                Measures how balanced player categories (A, B, C, D) are within and across teams. Team balance is prioritized over individual gaps.
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-green-600 w-6">5</span>
                  <span className="text-text-secondary">Perfect match - same team structure with gap ≤ 1 (AA vs AA, AB vs AB, CC vs CC, CD vs CD)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-green-600 w-6">4</span>
                  <span className="text-text-secondary">One category shift, all within 1 gap (AA vs AB, BB vs BC, CC vs CD)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-green-600 w-6">3</span>
                  <span className="text-text-secondary">Same structure with 2 gap (AC vs AC, AD vs AD) OR one shift with 2 gap (AB vs AC)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-green-600 w-6">2</span>
                  <span className="text-text-secondary">3 gap involved (AC vs AD, BC vs BD with 3 gap)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-green-600 w-6">1</span>
                  <span className="text-text-secondary">Unbalanced team structures (AC vs BB, AA vs BC) - different gap patterns</span>
                </div>
              </div>
            </div>

            {/* Player Interactions */}
            <div className="border-l-4 border-purple-500 pl-4">
              <h4 className="text-base font-semibold text-text-primary mb-2">3. Player Interactions (Variety)</h4>
              <p className="text-sm text-text-secondary mb-3">
                Measures game variety based on how frequently players have interacted with each other. Lower interaction percentage = better variety.
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-purple-600 w-6">5</span>
                  <span className="text-text-secondary">Interaction % ≤ 10% (excellent variety)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-purple-600 w-6">4</span>
                  <span className="text-text-secondary">Interaction % 11-20% (good variety)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-purple-600 w-6">3</span>
                  <span className="text-text-secondary">Interaction % 21-30% (moderate variety)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-purple-600 w-6">2</span>
                  <span className="text-text-secondary">Interaction % 31-40% (poor variety)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-purple-600 w-6">1</span>
                  <span className="text-text-secondary">Interaction % > 40% (very poor variety)</span>
                </div>
              </div>
            </div>

            {/* Game Type Balance */}
            <div className="border-l-4 border-orange-500 pl-4">
              <h4 className="text-base font-semibold text-text-primary mb-2">4. Game Type Balance</h4>
              <p className="text-sm text-text-secondary mb-3">
                Measures whether teams have matching game types (mixed, men's, women's).
              </p>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-orange-600 w-6">5</span>
                  <span className="text-text-secondary">Same game type (Mixed vs Mixed, Men's vs Men's, Women's vs Women's)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-orange-600 w-6">3</span>
                  <span className="text-text-secondary">Mixed vs Men's/Women's (acceptable but not ideal)</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-mono font-semibold text-orange-600 w-6">1</span>
                  <span className="text-text-secondary">Men's vs Women's (unbalanced, should be rare)</span>
                </div>
              </div>
            </div>
        </div>

        <div className="mt-6 p-4 bg-info-light border border-border-medium rounded-lg">
          <p className="text-sm text-text-primary">
            <strong>Total Rating:</strong> The sum of all four criteria (maximum 20). A rating of 16+ indicates high-quality matchmaking.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SimulationSummary;
